import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import AttachmentRow from "@/components/task/AttachmentRow";
import { useAttachmentUpload } from "@/components/task/useAttachmentUpload";
import { Paperclip, Upload } from "lucide-react";

export default function TaskAttachments({ task, canManage, isAssignee, onSaved, refreshActivity }) {
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const canUpload = canManage || isAssignee;

  useEffect(() => {
    let cancelled = false;
    setAttachments(task?.attachments || []);
    if (task?.task_id) {
      // Always re-fetch the authoritative list on open so a freshly uploaded file
      // (persisted server-side) shows even if the parent task object is stale.
      api.get(`/tasks/${task.task_id}/attachments`)
        .then(({ data }) => { if (!cancelled) setAttachments(Array.isArray(data) ? data : []); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [task?.task_id]);

  const { uploading, uploadPct, batch, uploadMany } = useAttachmentUpload({
    taskId: task?.task_id,
    onUploaded: (att) => setAttachments((a) => [...a, att]),
    onBatchDone: () => { refreshActivity(); onSaved(); },
  });

  const onFileInput = (e) => { const files = [...(e.target.files || [])]; if (files.length) uploadMany(files); e.target.value = ""; };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canUpload || uploading) return;
    const files = [...(e.dataTransfer.files || [])];
    if (files.length) uploadMany(files);
  };

  const deleteAttachment = async (fileId) => {
    try {
      await api.delete(`/tasks/${task.task_id}/attachments/${fileId}`);
      setAttachments((a) => a.filter((x) => x.file_id !== fileId));
      onSaved();
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div
      onDragOver={(e) => { if (canUpload && !uploading) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setDragOver(false); }}
      onDrop={onDrop}
      className={`relative rounded-sm transition-all ${dragOver ? "ring-2 ring-zinc-900 ring-offset-2" : ""}`}
      data-testid="attachments-dropzone"
    >
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <Paperclip className="h-3.5 w-3.5" /> Attachments
        </label>
        {canUpload && (
          <label className="text-xs font-semibold text-zinc-900 cursor-pointer inline-flex items-center gap-1 hover:underline" data-testid="upload-attachment-label">
            <Upload className="h-3.5 w-3.5" /> {uploading ? `Uploading ${batch && batch.total > 1 ? `${batch.current}/${batch.total} ` : ""}… ${uploadPct}%` : "Upload"}
            <input type="file" multiple className="hidden" onChange={onFileInput} disabled={uploading} data-testid="upload-attachment-input" />
          </label>
        )}
      </div>
      {uploading && (
        <div className="h-1.5 bg-zinc-100 rounded-full mt-2 overflow-hidden" data-testid="upload-progress">
          <div className="h-full bg-zinc-900 transition-all" style={{ width: `${uploadPct}%` }} />
        </div>
      )}
      <div className="space-y-2 mt-2">
        {attachments.length === 0 && <p className="text-xs text-zinc-400">{canUpload ? "No files attached. Drag & drop files here or click Upload." : "No files attached."}</p>}
        {attachments.map((att) => (
          <AttachmentRow key={att.file_id} att={att} canManage={canManage} onDelete={deleteAttachment}
            taskId={task?.task_id}
            onMarkupSaved={(newAtt) => { setAttachments((a) => [...a, newAtt]); refreshActivity(); onSaved(); }} />
        ))}
      </div>
      {dragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/85 border-2 border-dashed border-zinc-900 rounded-sm pointer-events-none">
          <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><Upload className="h-4 w-4" /> Drop files to attach</p>
        </div>
      )}
    </div>
  );
}
